import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, BookOpen, Star, Target, Sparkles, WifiOff, ChevronDown, ChevronUp,
  FileText, ClipboardCheck,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCabinStats } from '../hooks/cabin/useCabinStats';
import { useCabinModules } from '../hooks/cabin/useCabinModules';
import { useCabinProgress } from '../hooks/cabin/useCabinProgress';
import { interpolate, getLevelFromMiles } from '../utils/cabinI18n';
import StatCard from '../components/cabin/StatCard';
import MilestonePath from '../components/cabin/MilestonePath';
import ContinueLessonCard from '../components/cabin/ContinueLessonCard';
import ModuleCard from '../components/cabin/ModuleCard';
import SkeletonModuleCard from '../components/cabin/SkeletonModuleCard';
import DailyTasksWidget from '../components/cabin/DailyTasksWidget';
import RecentActivityFeed from '../components/cabin/RecentActivityFeed';
import ModuleDetailSheet from '../components/cabin/ModuleDetailSheet';

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};

export default function CabinDashboard() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();

  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useCabinStats();
  const { data: modules, isLoading: modulesLoading, isError: modulesError, refetch: refetchModules } = useCabinModules();
  const { data: progressData } = useCabinProgress();

  const [selectedModule, setSelectedModule] = useState(null);
  const [showAllModules, setShowAllModules] = useState(false);

  const isLoading = statsLoading || modulesLoading;
  const isError = statsError || modulesError;

  const handleRetry = () => {
    refetchStats();
    refetchModules();
  };

  const handleModuleClick = (mod) => {
    setSelectedModule(mod);
  };

  const handleModuleNavigate = (mod) => {
    setSelectedModule(null);
    navigate(`/programs/kabin-memurlugu/modules/${mod.slug}`);
  };

  // In-progress module for "continue" card
  const continueModule = modules?.find((m) => m.status === 'in_progress');
  const continueLesson = continueModule
    ? {
        // Backend returns resolved language title; fallback to _tr/_en for mock compat
        title: continueModule.title
          || (lang === 'tr' ? continueModule.title_tr : continueModule.title_en)
          || '',
        breadcrumb: `M${continueModule.order}`,
        progress: continueModule.progress,
        remainingMin: 12,
      }
    : null;

  // Modules to display: first 8 or all
  const displayedModules = showAllModules ? (modules || []) : (modules || []).slice(0, 8);

  const firstName = user?.name?.split(' ')[0] || (lang === 'tr' ? 'Öğrenci' : 'Student');
  const levelData = stats ? getLevelFromMiles(stats.totalMiles) : null;

  // Error state
  if (isError && !isLoading) {
    return (
      <div className="p-6 flex flex-col items-center gap-4 min-h-[60vh] justify-center">
        <WifiOff size={48} className="text-[var(--brand-danger)]" aria-hidden="true" />
        <h2 className="text-lg font-black text-[var(--text-main)]">{t('cabin.error.title')}</h2>
        <p className="text-sm text-[var(--text-muted)]">{t('cabin.error.sub')}</p>
        <button
          onClick={handleRetry}
          className="px-5 py-2 bg-[var(--brand-primary)] text-white font-bold rounded-xl"
          data-testid="cabin-error-retry"
        >
          {t('cabin.error.cta')}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen"
    >
      {/* ── Hero Banner ─────────────────────────────────── */}
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-4 pt-6 pb-16 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Top row: greeting */}
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">
              {interpolate(t('cabin.dashboard.greeting'), { name: firstName })}
            </h1>
            <p className="text-sm text-sky-200 mt-0.5">{t('cabin.dashboard.subtitle')}</p>
          </div>

          {/* Milestone path */}
          <div className="mt-5 bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
            <div className="flex items-center justify-between mb-2 text-xs text-sky-100">
              <span>
                {t('cabin.dashboard.current_level')}: {' '}
                <strong className="text-white">
                  {levelData ? t(`cabin.level.${levelData.key}`) : stats?.levelName_tr || '—'}
                </strong>
              </span>
              <span>
                {interpolate(t('cabin.dashboard.progress_pct'), {
                  pct: stats?.overallProgress || 0,
                })}
              </span>
            </div>
            <MilestonePath
              overallProgress={stats?.overallProgress || 0}
              completedLessons={stats?.completedLessons || 0}
              totalLessons={stats?.totalLessons || 22}
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* ── Stats Grid (negative margin overlap with hero) ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 -mt-8 z-10 relative mb-6">
          <StatCard
            icon={<Trophy size={22} strokeWidth={2.5} />}
            value={new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US').format(stats?.totalMiles || 0)}
            label={t('cabin.stats.total_miles')}
            ariaLabel={interpolate(t('cabin.a11y.total_miles'), { count: stats?.totalMiles || 0 })}
            testId="cabin-stat-miles"
          />
          <StatCard
            icon={<span className="text-xl">🔥</span>}
            value={stats?.streak || 0}
            label={t('cabin.stats.streak')}
            ariaLabel={interpolate(t('cabin.a11y.streak'), { days: stats?.streak || 0 })}
            testId="cabin-stat-streak"
          />
          <StatCard
            icon={<BookOpen size={22} strokeWidth={2.5} />}
            value={stats?.completedLessons || 0}
            label={t('cabin.stats.completed')}
            testId="cabin-stat-completed"
          />
          <StatCard
            icon={<Star size={22} strokeWidth={2.5} />}
            value={stats?.level || '—'}
            label={t('cabin.stats.level')}
            testId="cabin-stat-level"
          />
        </div>

        {/* ── Continue Learning ── */}
        <div className="mb-6">
          <ContinueLessonCard
            lesson={continueLesson}
            onContinue={() => {
              if (continueModule) {
                navigate(`/programs/kabin-memurlugu/modules/${continueModule.slug}`);
              } else {
                navigate('/programs/kabin-memurlugu/modules');
              }
            }}
          />
        </div>

        {/* ── Module Grid ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[var(--brand-secondary)]" aria-hidden="true" strokeWidth={2.5} />
              <h2 className="text-lg font-black text-[var(--text-main)]">
                {t('cabin.dashboard.modules_title')}
              </h2>
            </div>
            <button
              onClick={() => navigate('/programs/kabin-memurlugu/modules')}
              className="text-sm font-semibold text-[var(--brand-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid="cabin-view-all-modules"
            >
              {t('cabin.dashboard.view_all')} →
            </button>
          </div>

          {modulesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonModuleCard key={i} />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {displayedModules.map((mod) => (
                  <ModuleCard
                    key={mod.id}
                    module={mod}
                    onClick={() => handleModuleClick(mod)}
                  />
                ))}
              </div>

              {/* Show more/less toggle */}
              {(modules || []).length > 8 && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setShowAllModules((v) => !v)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-gray-200 text-sm font-semibold text-[var(--text-muted)] hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                    data-testid="cabin-toggle-modules"
                  >
                    {showAllModules ? (
                      <>
                        {t('cabin.dashboard.show_less')}
                        <ChevronUp size={16} aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        {interpolate(t('cabin.dashboard.show_more'), {
                          count: (modules || []).length - 8,
                        })}
                        <ChevronDown size={16} aria-hidden="true" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Roadmap Banner ── */}
        <div
          className="mb-6 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 cursor-pointer border-2 border-transparent hover:opacity-95 transition-opacity"
          onClick={() => navigate('/programs/kabin-memurlugu/roadmap')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/programs/kabin-memurlugu/roadmap');
            }
          }}
          aria-label={t('cabin.dashboard.roadmap_banner_title')}
          data-testid="cabin-roadmap-banner"
        >
          <div className="flex items-center gap-3">
            <Target size={24} className="text-white shrink-0" aria-hidden="true" />
            <div>
              <h3 className="text-base font-black text-white">
                {t('cabin.dashboard.roadmap_banner_title')}
              </h3>
              <p className="text-xs text-white/80">{t('cabin.dashboard.roadmap_banner_sub')}</p>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/programs/kabin-memurlugu/roadmap'); }}
            className="shrink-0 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-bold text-sm rounded-xl transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {t('cabin.dashboard.roadmap_banner_cta')} →
          </button>
        </div>

        {/* ── CV / Aday Profili Kartı ── */}
        <motion.div
          className="mb-6 rounded-2xl bg-white border-2 border-gray-200 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 cursor-pointer hover:border-cyan-300 hover:shadow-[2px_2px_0px_0px_rgba(14,165,233,0.15)] transition-all shadow-[2px_2px_0px_0px_rgba(0,0,0,0.06)]"
          onClick={() => navigate('/programs/kabin-memurlugu/cv')}
          whileHover={shouldReduce ? {} : { scale: 1.01 }}
          whileTap={shouldReduce ? {} : { scale: 0.99 }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/programs/kabin-memurlugu/cv');
            }
          }}
          aria-label={t('cabin.dashboard.cv_card.title')}
          data-testid="cabin-cv-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-50 border-2 border-cyan-200 flex items-center justify-center shrink-0">
              <FileText size={20} className="text-cyan-600" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-base font-black text-[var(--text-main)]">
                {t('cabin.dashboard.cv_card.title')}
              </h3>
              <p className="text-xs text-[var(--text-muted)]">
                {t('cabin.dashboard.cv_card.sub')}
              </p>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/programs/kabin-memurlugu/cv'); }}
            className="shrink-0 px-4 py-2 bg-cyan-500 border-2 border-cyan-700 text-white font-bold text-sm rounded-xl hover:bg-cyan-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]"
            tabIndex={-1}
            aria-hidden="true"
          >
            {t('cabin.dashboard.cv_card.cta')} →
          </button>
        </motion.div>

        {/* ── Bilgi Sınavı Kartı ── */}
        {stats?.examEligible && (
          <motion.div
            className="mb-6 rounded-2xl bg-white border-2 border-gray-200 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 cursor-pointer hover:border-emerald-300 hover:shadow-[2px_2px_0px_0px_rgba(16,185,129,0.15)] transition-all shadow-[2px_2px_0px_0px_rgba(0,0,0,0.06)]"
            onClick={() => navigate('/exam/course_cabin_main_v1')}
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.99 }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/exam/course_cabin_main_v1');
              }
            }}
            aria-label={t('cabin.dashboard.exam_card.title')}
            data-testid="cabin-exam-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center shrink-0">
                <ClipboardCheck size={20} className="text-emerald-600" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-base font-black text-[var(--text-main)]">
                  {t('cabin.dashboard.exam_card.title')}
                </h3>
                <p className="text-xs text-[var(--text-muted)]">
                  {t('cabin.dashboard.exam_card.sub')}
                </p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/exam/course_cabin_main_v1'); }}
              className="shrink-0 px-4 py-2 bg-emerald-500 border-2 border-emerald-700 text-white font-bold text-sm rounded-xl hover:bg-emerald-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]"
              tabIndex={-1}
              aria-hidden="true"
            >
              {t('cabin.dashboard.exam_card.cta')} →
            </button>
          </motion.div>
        )}

        {/* ── Daily Tasks Widget ── */}
        <div className="mb-6">
          <DailyTasksWidget defaultExpanded={false} />
        </div>

        {/* ── Recent Activity ── */}
        <div className="mb-6">
          <RecentActivityFeed activities={progressData?.recentActivity || []} />
        </div>

        {/* ── Footer motivational quote ── */}
        <p className="text-center text-sm text-[var(--text-muted)] italic px-4">
          "{t('cabin.dashboard.footer_quote')}"
        </p>
      </div>

      {/* Module Detail Sheet */}
      <ModuleDetailSheet
        module={selectedModule}
        open={!!selectedModule}
        onClose={() => setSelectedModule(null)}
        onNavigate={handleModuleNavigate}
      />
    </motion.div>
  );
}
